import React, { useEffect, useRef, useState } from 'react';
import emo1 from '../../assets/emotion1.webp';
import './EmotionDetection.css';

const EmotionDetection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [showPersona, setShowPersona] = useState(false);
  const [showCoachingCard, setShowCoachingCard] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sectionRef = useRef(null);

  // Single image: no auto-advance needed

  const fullText = "Developed an AI-powered system that detects and classifies human emotions in real time through facial expression analysis. Built with Convolutional Neural Networks (CNNs) and enhanced using pretrained architectures like MobileNetV2 and InceptionV3, the model achieves robust accuracy across diverse lighting and pose conditions. The project integrates OpenCV for image capture and preprocessing, while TensorFlow and Keras power efficient model training and deployment. Designed to bridge the gap between human affect and machine understanding, this system enables intelligent emotion-aware applications for healthcare, security, and human–computer interaction.";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.3,
        rootMargin: '-50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      setShowCoachingCard(true);

      let currentIndex = 0;
      const typeInterval = setInterval(() => {
        if (currentIndex <= fullText.length) {
          setTypedText(fullText.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(typeInterval);
        }
      }, 50);

      return () => clearInterval(typeInterval);
    }
  }, [isVisible, fullText]);

  return (
    <section className="emotion-section" ref={sectionRef}>
      <div className="emotion-container">
        <div className={`emotion-header ${isVisible ? 'animate-up' : ''}`}>
          <p className="emotion-subtitle">Emotion Detection System</p>
          <h2 className="emotion-title">Real-time facial emotion recognition using deep learning and computer vision.</h2>
        </div>
        <div className="emotion-content">
          <div className="emotion-left-section">
            <div className={`emotion-logo-section ${isVisible ? 'slide-in-left' : ''}`}>
              <div className="emotion-logo-large">
                <svg width="90" height="90" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="#a855f7" strokeWidth="2" />
                  <circle cx="9" cy="9" r="1.5" fill="#a855f7" />
                  <circle cx="15" cy="9" r="1.5" fill="#a855f7" />
                </svg>
              </div>
              <div className="emotion-project-info">
                <h3>Emotion AI Initiative</h3>
                <p>Advanced Facial Analysis</p>
              </div>
            </div>
          </div>

          <div className="emotion-center-section">
            <div className={`expanding-container ${typedText ? 'expanded' : ''}`}>
              <p className="typewriter-text">{typedText}</p>
              <span className="cursor">|</span>
            </div>

            <div className={`emotion-card ${showCoachingCard ? 'slide-up' : ''}`}>
              <div className="image-slideshow-container">
                <div className="slideshow-wrapper">
                  <div className={`slide active`}>
                    <img src={emo1} alt="Emotion Detection" className="slide-image" />
                  </div>
                </div>
                {/* Single image: dots removed */}
              </div>

              <div className="emotion-features-combined">
                <div className="feature-item">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" fill="#9333ea" />
                  </svg>
                  <span>Real-time facial expression analysis</span>
                </div>
                <div className="feature-item">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" fill="#9333ea" />
                  </svg>
                  <span>CNN-based emotion classification</span>
                </div>
                <div className="feature-item">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" fill="#9333ea" />
                  </svg>
                  <span>Multi-emotion recognition support</span>
                </div>
                <div className="feature-item">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" fill="#9333ea" />
                  </svg>
                  <span>Mental health monitoring applications</span>
                </div>
              </div>
            </div>

            <div className="github-btn-container">
              <a
                href="https://github.com/Mujakathali/DLV-Project"
                target="_blank"
                rel="noopener noreferrer"
                className="github-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                View on GitHub
              </a>
            </div>
          </div>

          <div className="emotion-right-section">
            {/* Add any right section content here if needed */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmotionDetection;
